import { useState } from "react";
import { toast } from "sonner";
import MaskedReveal from "@/components/MaskedReveal";
import FadeUp from "@/components/FadeUp";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatError, WHATSAPP_LINK } from "@/lib/api";
import { ArrowUpRight, AlertTriangle, PhoneCall } from "lucide-react";
import SEO from "@/components/SEO";
import JsonLd from "@/components/JsonLd";
import { breadcrumbListJsonLd, medicalWebPageJsonLd } from "@/data/schema";

const RED = "#8B1A1A";

export default function Urgent() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "", consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Please confirm consent to be contacted.");
    setSubmitting(true);
    try {
      await api.post("/urgent", { ...form, source: "urgent", urgency: "urgent", country: "India" });
      setDone(true);
      toast.success("Urgent enquiry received. The team will call you shortly.");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="urgent-page" className="bg-crimson-soft text-ink pt-28 md:pt-36 pb-28 min-h-screen">
      <SEO
        title="Urgent vascular evaluation — same-day Interventional Radiology | Dr Tegbir Sidhu"
        description="Same-day evaluation for urgent vascular presentations — critical limb ischaemia, sudden DVT, aortic emergencies. Direct WhatsApp line to Dr Sidhu's team. Max Super Speciality Hospital, Mohali."
        keywords={["urgent vascular consultation", "critical limb ischaemia India", "aortic emergency Mohali", "DVT urgent Chandigarh", "24 hour IR Punjab"]}
        url="/urgent"
      />
      <JsonLd data={[
        medicalWebPageJsonLd({ name: "Urgent vascular evaluation — Dr Tegbir Sidhu", description: "Same-day evaluation pathway for urgent vascular presentations in Chandigarh, Mohali and Punjab.", url: "/urgent" }),
        breadcrumbListJsonLd([{ label: "Home", to: "/" }, { label: "Urgent evaluation", to: "/urgent" }]),
      ]}/>
      {/* Emergency banner */}
      <div className="mx-auto max-w-[1440px] px-6 md:px-10">
        <FadeUp>
          <div className="flex items-start md:items-center gap-3 p-5 md:p-6 rounded-md bg-white/60 hairline">
            <AlertTriangle size={20} style={{color: RED}} className="flex-none mt-1 md:mt-0"/>
            <p className="text-sm md:text-base leading-relaxed">
              <span className="font-medium">If this is a life-threatening emergency</span> — severe chest pain, sudden weakness, uncontrolled bleeding, a cold and pale limb, or loss of consciousness — <span className="font-medium">please call your local emergency number (112 in India) or go to your nearest emergency department now.</span> This form is for time-sensitive vascular evaluation, not a substitute for emergency care.
            </p>
          </div>
        </FadeUp>

        {/* Hero */}
        <div className="mt-16 md:mt-24 grid grid-cols-12 gap-6">
          <FadeUp className="col-span-12 md:col-span-8">
            <div className="kbd" style={{color: RED}}>— Urgent evaluation</div>
            <h1 className="mt-8 font-serif text-6xl md:text-[8vw] leading-[0.94] tracking-tightest" style={{color: RED}}>
              <MaskedReveal text={"Time-sensitive"}/>
              <MaskedReveal text={"vascular"} delay={0.1}/>
              <MaskedReveal text={"cases only."} delay={0.2} className="italic font-normal"/>
            </h1>
            <p className="mt-8 text-lg md:text-xl leading-relaxed max-w-2xl text-ink">
              For patients with a fresh vascular diagnosis, a limb-threatening finding, or a specialist who has advised urgent intervention: submit here for a call from the practice within <span className="font-medium">60 minutes</span> during working hours, and same-day otherwise.
            </p>
          </FadeUp>

          <FadeUp className="col-span-12 md:col-span-4 md:pl-8" delay={0.2}>
            <div className="hairline p-6 md:p-8 bg-white/70">
              <div className="kbd" style={{color: RED}}>Fastest path</div>
              <div className="mt-4 font-serif text-3xl md:text-4xl tracking-tightest">Call now</div>
              <a data-testid="urgent-whatsapp" href={WHATSAPP_LINK("URGENT — I need Dr Sidhu's team to review a time-sensitive vascular case.")} target="_blank" rel="noreferrer" className="mt-6 btn-crimson w-full justify-center">
                <PhoneCall size={14}/>
                WhatsApp +91 96460 00444
              </a>
              <p className="mt-4 text-xs text-ink-muted leading-relaxed">
                Message will be seen by the practice coordinator, who escalates to Dr Sidhu directly.
              </p>
            </div>
          </FadeUp>
        </div>

        {/* Form */}
        <FadeUp className="mt-24">
          <div className="hairline p-6 md:p-10 bg-white/70">
            {done ? (
              <div data-testid="urgent-success" className="py-16 text-center">
                <div className="kbd" style={{color: RED}}>— Received</div>
                <h3 className="mt-6 font-serif text-4xl md:text-6xl tracking-tightest" style={{color: RED}}>The team is being paged.</h3>
                <p className="mt-6 text-ink-muted max-w-md mx-auto">
                  Please keep your phone with you. A member of Dr Sidhu's team will call within 60 minutes. If you can, please also send us your existing imaging by WhatsApp.
                </p>
                <a href={WHATSAPP_LINK("URGENT — I just submitted the urgent evaluation form. I can share imaging here.")} target="_blank" rel="noreferrer" className="btn-crimson mt-10 inline-flex">Send imaging on WhatsApp <ArrowUpRight size={14}/></a>
              </div>
            ) : (
              <form data-testid="urgent-form" onSubmit={submit} className="grid grid-cols-2 gap-6">
                <div className="col-span-2 md:col-span-1">
                  <Label className="kbd" style={{color: RED}}>Name</Label>
                  <Input data-testid="urgent-name" required value={form.name} onChange={(e) => update("name", e.target.value)} className="mt-2 h-12 rounded-none border-0 border-b border-ink/30 bg-transparent focus-visible:ring-0 focus-visible:border-ink text-lg px-0"/>
                </div>
                <div className="col-span-2 md:col-span-1">
                  <Label className="kbd" style={{color: RED}}>Phone (reachable now)</Label>
                  <Input data-testid="urgent-phone" required value={form.phone} onChange={(e) => update("phone", e.target.value)} className="mt-2 h-12 rounded-none border-0 border-b border-ink/30 bg-transparent focus-visible:ring-0 focus-visible:border-ink text-lg px-0"/>
                </div>
                <div className="col-span-2">
                  <Label className="kbd" style={{color: RED}}>Email</Label>
                  <Input data-testid="urgent-email" type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} className="mt-2 h-12 rounded-none border-0 border-b border-ink/30 bg-transparent focus-visible:ring-0 focus-visible:border-ink text-lg px-0"/>
                </div>
                <div className="col-span-2">
                  <Label className="kbd" style={{color: RED}}>What's happening?</Label>
                  <Textarea data-testid="urgent-message" required rows={4} value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Diagnosis (if known), symptoms, when they started, existing imaging."  className="mt-2 rounded-none border-0 border-b border-ink/30 bg-transparent focus-visible:ring-0 focus-visible:border-ink text-lg px-0 resize-none"/>
                </div>
                <div className="col-span-2 flex items-start gap-3 mt-2">
                  <Checkbox id="uconsent" data-testid="urgent-consent" checked={form.consent} onCheckedChange={(v) => update("consent", Boolean(v))} className="mt-1"/>
                  <Label htmlFor="uconsent" className="text-sm text-ink-muted leading-relaxed">
                    I understand this is not emergency care and I consent to being called back on the number above.
                  </Label>
                </div>
                <div className="col-span-2 flex flex-wrap items-center justify-between gap-4 mt-4">
                  <div className="kbd" style={{color: RED}}>Response within 60 minutes (working hours)</div>
                  <button data-testid="urgent-submit" type="submit" disabled={submitting} className="btn-crimson disabled:opacity-60">
                    {submitting ? "Sending…" : "Page the team"}
                    <ArrowUpRight size={14}/>
                  </button>
                </div>
              </form>
            )}
          </div>
        </FadeUp>
      </div>
    </div>
  );
}
